// frontend/src/utils/filterEngine.js
// Central place for filter option lists + value extraction, shared by FilterContainer
// (rendering/counting) and MediaListPage (actual filtering) so the two stay in sync
// and combined/composite lists (multiple MediaItem.type values in one list) "just work".

export const CONDITION_OPTIONS = [
  { value: 'Excellent', label: 'Excellent', icon: '/static/icons/circle-check-solid.svg' },
  { value: 'Good', label: 'Good', icon: '/static/icons/check-solid.svg' },
  { value: 'Okay', label: 'Okay', icon: '/static/icons/circle-solid.svg' },
  { value: 'Bad', label: 'Bad', icon: '/static/icons/circle-xmark-solid.svg' },
  { value: 'Poor', label: 'Poor', icon: '/static/icons/xmark-solid.svg' },
];

export const PLATFORM_OPTIONS = [
  { value: 'psp', label: 'PSP', icon: '/static/icons/playstation.svg' },
  { value: 'vita', label: 'PS Vita', icon: '/static/icons/playstationvita.svg' },
  { value: 'nds', label: 'Nintendo DS', icon: '/static/icons/nintendo-ds.svg' },
  { value: 'switch', label: 'Switch', icon: '/static/icons/nintendoswitch.svg' },
  { value: 'ps3', label: 'PS3', icon: '/static/icons/playstation.svg' },
  { value: 'gameboy', label: 'Game Boy', icon: '/static/icons/gamepad-solid.svg' },
];

export const PLATFORM_PLAYED_OPTIONS = [
  { value: 'windows', label: 'Windows', icon: '/static/icons/windows.svg' },
  { value: 'linux', label: 'Linux', icon: '/static/icons/linux.svg' },
  { value: 'steamdeck', label: 'Steam Deck', icon: '/static/icons/steamdeck.svg' },
  { value: 'mac', label: 'Mac', icon: '/static/icons/apple.svg' },
];

export const ANIME_MEDIA_TYPE_OPTIONS = [
  { value: 'tv', label: 'TV', icon: '/static/icons/tv-solid.svg' },
  { value: 'movie', label: 'Movie', icon: '/static/icons/film-solid.svg' },
  { value: 'ova', label: 'OVA', icon: '/static/icons/video-solid.svg' },
  { value: 'special', label: 'Special', icon: '/static/icons/star-half-stroke-solid.svg' },
  { value: 'unknown', label: 'Unknown', icon: '/static/icons/clipboard-question-solid.svg' },
];

export const FORMAT_OPTIONS = [
  { value: 'true', label: 'Animation', icon: '/static/icons/video-solid.svg' },
  { value: 'false', label: 'Live-Action', icon: '/static/icons/film-solid.svg' },
];

export const SEASON_OPTIONS = [
  { value: 'Single Season', label: 'Single Season', icon: '/static/icons/film-solid.svg' },
  { value: 'Multiple Seasons', label: 'Multiple Seasons', icon: '/static/icons/layer-group-solid.svg' },
];

export const EXPLICIT_OPTIONS = [
  { value: 'false', label: 'Safe', icon: '/static/icons/shield-solid.svg' },
  { value: 'true', label: '18+', icon: '/static/icons/triangle-exclamation-solid.svg' },
];

// Icons for the "Status" filter group, keyed by the exact status string (shared across media types).
export const STATUS_ICONS = {
  Finished: '/static/icons/trophy-solid.svg',
  Completed: '/static/icons/trophy-solid.svg',
  Watching: '/static/icons/eye-solid.svg',
  Playing: '/static/icons/eye-solid.svg',
  Reading: '/static/icons/book-open-solid.svg',
  'On Hold': '/static/icons/eye-slash-solid.svg',
  'Not Playing': '/static/icons/eye-slash-solid.svg',
  Dropped: '/static/icons/circle-xmark-solid.svg',
  Forgotten: '/static/icons/ghost-solid.svg',
  'Plan to Watch': '/static/icons/list-solid.svg',
  'Plan to Read': '/static/icons/list-solid.svg',
  Unplayed: '/static/icons/list-solid.svg',
  Listened: '/static/icons/check-double-solid.svg',
  'Not Listened': '/static/icons/circle-xmark-solid.svg',
  Unknown: '/static/icons/clipboard-question-solid.svg',
};

// Icons for the "Ownership" filter group, keyed by the exact ownership_status string.
export const OWNERSHIP_ICONS = {
  'Not Owned': '/static/icons/circle-xmark-solid.svg',
  Wishlisted: '/static/icons/star-solid.svg',
  Collecting: '/static/icons/book-solid.svg',
  Owned: '/static/icons/check-double-solid.svg',
};

// Distinct MediaItem.type values present in a list of items (e.g. ['Anime', 'ScreenMedia']).
export const getPresentTypes = (items) => Array.from(new Set((items || []).map((i) => i.type)));

// Comma-separated string fields (across every sub-model) that hold categorization data
// (genre/theme/tag-style values) - merged onto the flat item by serialize_media_list.
export const TAG_SOURCE_FIELDS = ['genre', 'tags', 'studios', 'meta_tags'];

const splitTagField = (raw) => {
  if (!raw) return [];
  return String(raw).split(',').map((v) => v.trim()).filter(Boolean);
};

// Distinct genre/tag/studio/meta-tag values an item has, lower-cased for matching. Items whose
// type doesn't have any of these fields (e.g. a Music item with no genre set) simply return [].
export const getItemTagValues = (item) => {
  const values = new Set();
  TAG_SOURCE_FIELDS.forEach((field) => splitTagField(item[field]).forEach((v) => values.add(v.toLowerCase())));
  return Array.from(values);
};

// Map of lower-cased tag value -> nicest display label (first-seen original casing), built across
// a list of items so filter chips/autocomplete suggestions show natural casing instead of lower-case.
export const getTagLabelMap = (items) => {
  const map = new Map();
  (items || []).forEach((item) => {
    TAG_SOURCE_FIELDS.forEach((field) => {
      splitTagField(item[field]).forEach((v) => {
        const key = v.toLowerCase();
        if (!map.has(key)) map.set(key, v);
      });
    });
  });
  return map;
};

// Every distinct genre/tag/studio/meta-tag value present across a list of items (typically the
// currently loaded media type's items) - used to populate the tag filter's autocomplete, so
// suggestions are automatically scoped to whatever's actually possible for the current list.
export const getAllTagValues = (items) => {
  const values = new Set();
  (items || []).forEach((item) => getItemTagValues(item).forEach((v) => values.add(v)));
  return Array.from(values).sort();
};

// Fields searched by the free-text search box, beyond the base title - alternate titles
// (EN/JP/MAL/TMDB) plus people/company fields, so search isn't limited to the primary title.
const SEARCH_FIELDS = [
  'title', 'title_en', 'title_ja', 'title_mal', 'title_tmdb', 'original_title',
  'developer', 'publisher', 'studios', 'authors', 'artist', 'album', 'label', 'creators', 'cast',
];

const searchFieldsMatch = (source, q) =>
  SEARCH_FIELDS.some((field) => {
    const val = source[field];
    return val != null && String(val).toLowerCase().includes(q);
  });

// Whether an item matches a free-text search query - checks title/alt-titles/people fields on the
// item itself AND on each of its seasons (Anime/ScreenMedia), so a season-specific alternate title
// still surfaces the parent series. Missing fields on a given media type are simply skipped.
export const itemMatchesSearch = (item, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  if (searchFieldsMatch(item, q)) return true;
  return (item.seasons || []).some((season) => searchFieldsMatch(season, q));
};

/**
 * Returns the value(s) an item has for a given filter id.
 * - Arrays mean "matches if ANY of these values is selected" (multi-value membership, e.g. platforms played on).
 * - `null`/`undefined` means the item doesn't have this attribute at all, so it never matches a selection
 *   in that category (an item missing a field never satisfies a filter on that field).
 */
export const getFilterValue = (filterId, item) => {
  switch (filterId) {
    case 'status':
      return item.status;
    case 'ownership_status':
      return item.ownership_status || 'Not Owned';
    case 'media_type':
      // Anime keeps its own granular type; ScreenMedia (TV/Movie) items merge into the
      // same 'tv'/'movie' buckets when viewed together with Anime (e.g. ShowsAndMovies).
      if (item.type === 'Anime') return item.media_type || 'unknown';
      if (item.type === 'ScreenMedia') return item.media_format?.toLowerCase() === 'movie' ? 'movie' : 'tv';
      return null;
    case 'is_animated':
      // Anime is always animated, even though it has no is_animated column of its own.
      if (item.type === 'Anime') return 'true';
      if (item.type === 'ScreenMedia') return String(item.is_animated ?? false);
      return null;
    case 'seasons':
      return item.seasons?.length > 1 ? 'Multiple Seasons' : 'Single Season';
    case 'condition':
      return item.condition || null;
    case 'platform':
      return item.type === 'PhysicalGame' ? item.platform || null : null;
    case 'achievements':
      return item.type === 'SteamGame' ? (item.total_achievements > 0 ? 'has-achievements' : '') : null;
    case 'perfect_game':
      return item.type === 'SteamGame'
        ? item.total_achievements > 0 && item.achievements_unlocked >= item.total_achievements
          ? 'perfect'
          : ''
        : null;
    case 'platform_played': {
      if (item.type !== 'SteamGame') return null;
      const values = [];
      if (item.playtime_windows > 0) values.push('windows');
      if (item.playtime_linux > 0) values.push('linux');
      if (item.playtime_deck > 0) values.push('steamdeck');
      if (item.playtime_mac > 0) values.push('mac');
      return values;
    }
    case 'favorite':
      return item.is_favorite ? 'true' : 'false';
    case 'explicit':
      return String(item.explicit ?? false);
    case 'genre_tag':
      return getItemTagValues(item);
    default:
      return undefined;
  }
};

/**
 * Whether an item matches the selected values for a single filter category.
 * - For multi-value fields (arrays, e.g. platform_played or genre_tag), `logic` decides whether
 *   ANY selected value must be present ('or') or ALL of them must be present ('and').
 * - For single-value fields, membership is always OR (item's one value is in the selected set).
 */
export const itemMatchesFilter = (filterId, item, selectedValues, logic = 'or') => {
  const value = getFilterValue(filterId, item);
  if (Array.isArray(value)) {
    return logic === 'and'
      ? selectedValues.every((v) => value.includes(v))
      : selectedValues.some((v) => value.includes(v));
  }
  return selectedValues.includes(value);
};

export const countMatching = (items, filterId, value) =>
  items.filter((item) => itemMatchesFilter(filterId, item, [value])).length;
